import { Crosshair, Eye, Flame, FlaskConical, Gavel, Moon, ShieldCheck, Sun, UsersRound, Crown } from "lucide-react";
import type { GamePhase } from "@ma-soi/game-core";
import { useId } from "react";

// Bốn đường xé có độ cong, độ sâu và điểm kết thúc khác nhau để dấu vuốt không thành các dải đều nhau.
const CLAW_CUTS = [
  {
    edge: "M65 15C60 44 51 68 45 94L47 92 40 113 40 122 34 135 33 149C26 173 22 184 17 194C21 171 23 151 28 134L27 132 32 115 34 96C42 59 54 32 65 15Z",
    core: "M60 30C51 61 42 94 36 123L36 132 30 147C25 166 21 181 18 190C24 164 26 142 31 122L34 99C41 70 51 44 60 30Z",
    light: "M59 30C50 52 42 75 36 96M31 118 28 133M26 145 21 174",
  },
  {
    edge: "M94 8C88 37 80 65 75 90L72 99 74 100 67 122 66 136 61 143C55 171 47 205 42 226C42 203 46 174 50 151L49 145 53 127 54 112 58 104C64 69 79 31 94 8Z",
    core: "M88 26C78 56 70 86 64 114L63 128 58 142C51 173 47 202 43 221C47 188 48 165 53 142L56 126 58 110C64 83 77 45 88 26Z",
    light: "M85 28C74 50 66 76 60 103M56 117 54 129 53 138M49 162 44 203",
  },
  {
    edge: "M121 19C116 48 110 77 105 107L101 118 103 120 96 143 96 157 92 164C87 190 82 216 79 232C77 209 80 185 81 168L80 161 84 143 85 127 88 122C93 85 105 43 121 19Z",
    core: "M116 35C106 65 100 99 94 130L91 148 90 160C85 185 82 215 79 227C80 197 83 168 86 149L87 129C92 99 103 60 116 35Z",
    light: "M113 38C103 58 96 86 90 113M86 137 84 149M82 170 80 206",
  },
  {
    edge: "M148 44C141 68 136 87 132 109L133 112 128 131 129 140 124 153C121 172 119 190 117 202C113 185 114 163 114 153L113 147 118 126 119 111C125 85 135 62 148 44Z",
    core: "M143 56C132 82 127 108 123 131L121 146C118 165 117 185 117 197C116 176 117 155 118 144L120 124C125 96 132 75 143 56Z",
    light: "M140 60C132 75 124 95 121 110M118 131 116 146M116 159 115 177",
  },
];

export function ClawMark({ className = "" }: { className?: string }) {
  const id = useId();
  return <svg className={`claw-mark ${className}`} viewBox="0 0 160 240" fill="none" aria-hidden="true" focusable="false">
    <defs>
      <linearGradient id={`${id}-rim`} x1="132" y1="20" x2="40" y2="219" gradientUnits="userSpaceOnUse">
        <stop className="claw-rim-bright" offset="0" /><stop className="claw-rim-mid" offset=".28" />
        <stop className="claw-rim-dark" offset=".57" /><stop className="claw-rim-mid" offset=".78" /><stop className="claw-rim-dark" offset="1" />
      </linearGradient>
      <linearGradient id={`${id}-recess`} x1="52" y1="90" x2="111" y2="114" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#07060b" /><stop className="claw-recess" offset=".55" /><stop offset="1" stopColor="#07060b" />
      </linearGradient>
      <pattern id={`${id}-grain`} width="17" height="23" patternUnits="userSpaceOnUse">
        <path d="m1 3 7 1m4 5 5-1M2 15l9 2m1 5 4-1" stroke="#08070d" strokeWidth=".8" opacity=".32" />
      </pattern>
    </defs>
    {CLAW_CUTS.map((cut, index) => <g key={index}>
      <path d={cut.edge} stroke="#030409" strokeWidth="2.2" strokeLinejoin="round" opacity=".5" />
      <path d={cut.edge} fill={`url(#${id}-rim)`} />
      <path d={cut.edge} fill={`url(#${id}-grain)`} />
      <path d={cut.core} fill={`url(#${id}-recess)`} />
      <path className="claw-edge-light" d={cut.light} strokeWidth="1.15" strokeLinecap="round" />
    </g>)}
    <path className="claw-fibers" d="m37 91-5 5m7 21-4 6m36-22-4 8m-14 35-5 8m44-25-4 9m4 31-4 7m38-67-3 6m-1 28-3 7" strokeWidth=".8" strokeLinecap="round" />
  </svg>;
}

export function PhaseSigil({ phase }: { phase: GamePhase }) {
  const Icon = ({ NIGHT_GUARD: ShieldCheck, NIGHT_SEER: Eye, NIGHT_WITCH: FlaskConical, HUNTER_REACTION: Crosshair, NIGHT_RESOLUTION: Flame, MORNING: Sun, DISCUSSION: UsersRound, VOTING: Gavel, VOTE_RESULT: Gavel, ENDED: Crown } as Partial<Record<GamePhase, typeof Moon>>)[phase] ?? Moon;
  return <span className={`phase-sigil sigil-${phase.toLowerCase()}`} aria-hidden="true">{phase === "NIGHT_WOLVES" ? <ClawMark /> : <Icon />}</span>;
}

export function EliminationAccent({ active = false }: { active?: boolean }) {
  return <span className={`elimination-accent ${active ? "is-striking" : ""}`} aria-hidden="true"><ClawMark /></span>;
}
